class Utils {
  static refreshPage() {
    window.location.reload();
  }

  static objectToQueryString = (obj) => {
    return Object.keys(obj)
      .filter((key) => encodeURIComponent(obj[key]))
      .map(
        (key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`
      )
      .join("&");
  };

  static getQueryStringParams = (location) => {
    const searchParams = new URLSearchParams(location.search);
    const params = {};

    for (let [key, value] of searchParams.entries()) {
      params[key] = value;
    }

    return params;
  };

  static fixCamelCase = (input) => {
    return input
      .replace(/([a-z])([A-Z])/g, "$1 $2") // Separate camelCase words
      .replace(/\b\w/g, (match) => match.toUpperCase()); // Capitalize the first letter of each word
  };

  static convertToDateTime(inputString) {
    if (!inputString) return "-";

    // Parse the input string as a Date object
    const inputDate = new Date(`${inputString}Z`);

    // Check if the input is a valid date
    if (isNaN(inputDate.getTime())) {
      return "Invalid date";
    }

    // Format the date in MM/DD/YYYY format
    const formattedDate = `${(inputDate.getMonth() + 1)
      .toString()
      .padStart(2, "0")}/${inputDate
      .getDate()
      .toString()
      .padStart(2, "0")}/${inputDate.getFullYear()}`;

    // Format the time in 12-hour format with AM/PM
    const hours = inputDate.getHours() % 12 || 12;
    const minutes = inputDate.getMinutes().toString().padStart(2, "0");
    const amPm = inputDate.getHours() >= 12 ? "PM" : "AM";
    const formattedTime = `${hours}:${minutes} ${amPm}`;

    // Combine the formatted date and time
    const usDateTime = `${formattedDate} ${formattedTime}`;

    return usDateTime;
  }

  static getSafeRedirectUrl(url) {
    if (!url) return Utils.getHomeRoute();
    try {
      const parsed = new URL(url, "https://app.placeholder");
      if (parsed.hostname !== "app.placeholder") return Utils.getHomeRoute();
      return parsed.pathname + parsed.search + parsed.hash;
    } catch {
      return Utils.getHomeRoute();
    }
  }

  // "Try our new experience" preference: which page counts as "home" once there's no more
  // specific destination (post-login, after Okta/Azure AD auth, MFA success, impersonation
  // start/stop, client switch, ...). Plain localStorage (not React Context) so it's readable
  // from anywhere, including non-component code — Redux thunks (user.actions.js) and
  // oktaAuthWrapper.jsx's raw `window.location.href` redirects, neither of which can reach a
  // Context. WorkspaceExperienceContext reads/writes the same key for reactive UI (the
  // Header toggle, the Dashboard banner) — single source of truth, two ways to read it.
  static WORKSPACE_EXPERIENCE_KEY = "useWorkspaceExperience";

  static isWorkspaceExperienceEnabled() {
    try {
      return localStorage.getItem(Utils.WORKSPACE_EXPERIENCE_KEY) === "true";
    } catch {
      return false;
    }
  }

  static setWorkspaceExperienceEnabled(enabled) {
    try {
      localStorage.setItem(Utils.WORKSPACE_EXPERIENCE_KEY, enabled ? "true" : "false");
    } catch {
      // localStorage unavailable (private mode, quota, ...) — preference just won't persist
    }
  }

  static getHomeRoute() {
    return Utils.isWorkspaceExperienceEnabled() ? "/workspace" : "/dashboard";
  }

  static validateReturnToUrl(url, allowedPrefixes) {
    if (!url || !Array.isArray(allowedPrefixes) || allowedPrefixes.length === 0) return false;
    try {
      const decoded = decodeURIComponent(url);
      return allowedPrefixes.some((prefix) => decoded.startsWith(prefix));
    } catch {
      return false;
    }
  }

  static async isPasswordBreached(password) {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest("SHA-1", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hash = hashArray
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
      .toUpperCase();
    const prefix = hash.slice(0, 5);
    const suffix = hash.slice(5);
    const response = await fetch(
      `https://api.pwnedpasswords.com/range/${prefix}`
    );
    const text = await response.text();
    return text.split("\n").some((line) => line.split(":")[0] === suffix);
  }

  static isEmailValid(email) {
    // Regular expression for basic email validation
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  }

  static formatDateToInput = (dateString) => {
    if (!dateString) return null;

    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = `0${date.getMonth() + 1}`.slice(-2);
    const day = `0${date.getDate()}`.slice(-2);
    return `${year}-${month}-${day}`;
  };

  static convertToDateOnly(dateTimeString) {
    if (!dateTimeString) return "-";

    // Parse the input string into a Date object
    const date = new Date(dateTimeString);

    // Add timezone offset to adjust for the correct local time
    const timezoneOffset = date.getTimezoneOffset() * 60000; // convert to milliseconds
    const adjustedDate = new Date(date.getTime() - timezoneOffset);

    // Format the adjusted date into mm/dd/yyyy
    const year = adjustedDate.getUTCFullYear();
    const month = String(adjustedDate.getUTCMonth() + 1).padStart(2, "0");
    const day = String(adjustedDate.getUTCDate()).padStart(2, "0");

    return `${month}/${day}/${year}`;
  }

  // Pure string conversion for date-ONLY values (no time component) -- unlike convertToDateOnly
  // above, this never goes through a Date object, so there's no UTC-midnight-parsing/timezone
  // shift to get wrong. Round-trips a native <input type="date"> value ("YYYY-MM-DD") against
  // this app's usual MM/DD/YYYY display/storage format.
  static isoDateToDisplay(isoDate) {
    if (!isoDate) return "";
    const [year, month, day] = isoDate.split("-");
    if (!day) return "";
    return `${month}/${day}/${year}`;
  }

  static displayDateToIso(displayDate) {
    if (!displayDate) return "";
    const [month, day, year] = displayDate.split("/");
    if (!year) return "";
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }
}

export default Utils;
